import ExcelJS from "exceljs";
import api from "../services/api";

export interface VoterOption {
  id: string;
  option_text: string;
  image_url?: string;
  vote_count: number;
  percentage: number;
  voters: Array<{ user_id: number; full_name: string; employee_id: string }>;
}

export interface PollVoterData {
  poll_id: number;
  title: string;
  description?: string;
  total_votes: number;
  allow_multiple: boolean;
  created_at: string;
  closes_at?: string;
  options: VoterOption[];
  non_voters: Array<{
    user_id: number;
    full_name: string;
    employee_id: string;
  }>;
}

// ── Constants ────────────────────────────────────────────────────────────────
const DARK_BLUE = "FF1E3A5F";
const YELLOW = "FFFBBF24";
const LIGHT_BLUE = "FFE8F0FE";
const WHITE = "FFFFFFFF";

// ── Helpers ──────────────────────────────────────────────────────────────────
type ExcelImageExt = "jpeg" | "png" | "gif";

const styleHeader = (row: ExcelJS.Row, bg = DARK_BLUE) => {
  row.height = 24;
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: WHITE }, size: 11 };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: true,
    };
    cell.border = { bottom: { style: "thin", color: { argb: WHITE } } };
  });
};

const zebra = (row: ExcelJS.Row, i: number, highlight = false) => {
  const bg = highlight ? YELLOW : i % 2 === 0 ? LIGHT_BLUE : WHITE;
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
    cell.alignment = { vertical: "middle", wrapText: true };
  });
};

/**
 * Fetch an image for Excel embedding.
 * - Relative URLs (local dev): fetched via api instance, same origin
 * - Absolute URLs (R2 presigned): routed through backend proxy to avoid CORS
 */
const fetchImageBase64 = async (
  imageUrl: string,
): Promise<{ b64: string; ext: ExcelImageExt } | null> => {
  try {
    let response;

    if (imageUrl.startsWith("http")) {
      // Absolute URL (R2 presigned) — go through backend proxy to avoid CORS
      // Backend receives the storage key, not the presigned URL
      // Extract the key from the URL path (everything after the bucket hostname)
      const storageKey = new URL(imageUrl).pathname.replace(/^\//, "");
      response = await api.get(`/uploads/proxy`, {
        params: { key: storageKey },
        responseType: "arraybuffer",
      });
    } else {
      // Relative path (local dev static file)
      response = await api.get(imageUrl, {
        responseType: "arraybuffer",
        baseURL: api.defaults.baseURL?.replace(/\/api\/?$/, ""),
      });
    }

    const contentType: string =
      response.headers["content-type"] || "image/jpeg";
    const extMap: Record<string, ExcelImageExt> = {
      "image/jpeg": "jpeg",
      "image/jpg": "jpeg",
      "image/png": "png",
      "image/gif": "gif",
      "image/webp": "jpeg",
    };
    const ext = extMap[contentType.split(";")[0].trim()] ?? "jpeg";

    const bytes = new Uint8Array(response.data as ArrayBuffer);
    let binary = "";
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return { b64: btoa(binary), ext };
  } catch {
    return null;
  }
};

// ── Main export function ─────────────────────────────────────────────────────
export const exportPollToExcel = async (data: PollVoterData): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "HR System";
  workbook.created = new Date();

  const sorted = [...data.options].sort((a, b) => b.vote_count - a.vote_count);

  // Fetch unique images in parallel (one request per option, not per voter)
  const optionImages: Record<
    string,
    { b64: string; ext: ExcelImageExt } | null
  > = {};
  await Promise.all(
    data.options.map(async (opt) => {
      optionImages[opt.id] = opt.image_url
        ? await fetchImageBase64(opt.image_url)
        : null;
    }),
  );

  // ── Sheet 1: Side-by-side mini-tables ─────────────────────────────────
  // Each option = 2 columns (Emp ID | Name) + 1 gap column
  // All options share the same rows so they sit next to each other
  const sheet = workbook.addWorksheet("🗳️ Poll Results");

  const OPT_COLS = 2; // columns per option (ID + Name)
  const GAP_COLS = 1; // blank separator column between options
  const STRIDE = OPT_COLS + GAP_COLS; // 3 cols per option slot
  const ID_COL_W = 12;
  const NAME_COL_W = 24;
  const GAP_COL_W = 2;
  const IMG_H = 80;

  // Set column widths for every option slot
  sorted.forEach((_, i) => {
    const base = i * STRIDE; // 0-based
    sheet.getColumn(base + 1).width = ID_COL_W;
    sheet.getColumn(base + 2).width = NAME_COL_W;
    if (i < sorted.length - 1) {
      sheet.getColumn(base + 3).width = GAP_COL_W;
    }
  });

  const maxVoters = Math.max(...sorted.map((o) => o.voters.length), 1);

  // Pre-build all rows we'll need:
  // Row 1 = image header
  // Row 2 = option label + stats
  // Row 3 = column headers (Emp ID | Name)
  // Row 4..N = voter data rows (padded to maxVoters)
  // Row N+1 = empty votes message for options with no voters

  const HEADER_ROWS = 3; // image + label + col headers
  const totalRows = HEADER_ROWS + Math.max(maxVoters, 1);

  // Add all rows upfront as empty
  for (let r = 0; r < totalRows; r++) {
    const row = sheet.addRow([]);
    if (r === 0)
      row.height = IMG_H; // image row
    else if (r === 1)
      row.height = 22; // label row
    else if (r === 2)
      row.height = 18; // col header row
    else row.height = 18; // data rows
  }

  // Helper to get a cell by (1-based row, 1-based col)
  const cell = (row: number, col: number) => sheet.getRow(row).getCell(col);

  sorted.forEach((opt, i) => {
    const isLeader = i === 0 && opt.vote_count > 0;
    const base = i * STRIDE + 1; // 1-based start col for this option
    const idCol = base;
    const nameCol = base + 1;

    const headerBg = isLeader ? YELLOW : DARK_BLUE;
    const headerFg = isLeader ? "FF1E3A5F" : WHITE;
    const subBg = isLeader ? "FFFDE68A" : "FF1E40AF";

    // ── Row 1: Image ───────────────────────────────────────────────────
    sheet.mergeCells(1, idCol, 1, nameCol);
    const imgCell = cell(1, idCol);
    imgCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF0F172A" },
    };

    const imgData = optionImages[opt.id];
    if (imgData) {
      const imageId = workbook.addImage({
        base64: imgData.b64,
        extension: imgData.ext,
      });
      sheet.addImage(imageId, {
        tl: { col: idCol - 1, row: 0 } as any,
        br: { col: nameCol, row: 1 } as any,
        editAs: "oneCell",
      });
    }

    // ── Row 2: Label ───────────────────────────────────────────────────
    sheet.mergeCells(2, idCol, 2, nameCol);
    const labelCell = cell(2, idCol);
    const labelText = [
      isLeader ? "🏆 " : "",
      opt.option_text || `Option ${i + 1}`,
      `  •  ${opt.vote_count} vote${opt.vote_count !== 1 ? "s" : ""}`,
      `  •  ${opt.percentage.toFixed(1)}%`,
    ].join("");
    labelCell.value = labelText;
    labelCell.font = { bold: true, color: { argb: headerFg }, size: 10 };
    labelCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: headerBg },
    };
    labelCell.alignment = { horizontal: "center", vertical: "middle" };

    // ── Row 3: Column headers ──────────────────────────────────────────
    const hId = cell(3, idCol);
    const hName = cell(3, nameCol);
    [hId, hName].forEach((c, ci) => {
      c.value = ci === 0 ? "מספר עובד" : "שם מלא";
      c.font = { bold: true, color: { argb: WHITE }, size: 9 };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: subBg } };
      c.alignment = { horizontal: "center", vertical: "middle" };
      c.border = { bottom: { style: "thin", color: { argb: WHITE } } };
    });

    // ── Rows 4+: Voter data ────────────────────────────────────────────
    if (opt.voters.length === 0) {
      sheet.mergeCells(4, idCol, 4, nameCol);
      const emptyCell = cell(4, idCol);
      emptyCell.value = "No votes";
      emptyCell.font = { italic: true, color: { argb: "FF94A3B8" }, size: 9 };
      emptyCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFF8FAFC" },
      };
      emptyCell.alignment = { horizontal: "center", vertical: "middle" };
    } else {
      opt.voters.forEach((voter, vi) => {
        const rowNum = HEADER_ROWS + 1 + vi; // 1-based
        const bg = vi % 2 === 0 ? LIGHT_BLUE : WHITE;

        const cId = cell(rowNum, idCol);
        const cName = cell(rowNum, nameCol);

        cId.value = voter.employee_id;
        cName.value = voter.full_name;

        [cId, cName].forEach((c) => {
          c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
          c.alignment = { vertical: "middle" };
          c.font = { size: 9 };
        });
        cId.alignment = { horizontal: "center", vertical: "middle" };
      });
    }
  });

  // ── Sheet 2: Summary ────────────────────────────────────────────────────
  const summarySheet = workbook.addWorksheet("📋 Summary");
  summarySheet.columns = [
    { key: "f", width: 26 },
    { key: "v", width: 42 },
  ];

  const sumTitle = summarySheet.addRow(["Poll Export Summary", ""]);
  summarySheet.mergeCells("A1:B1");
  styleHeader(sumTitle);
  sumTitle.getCell(1).alignment = { horizontal: "center", vertical: "middle" };

  const summaryRows: [string, string | number][] = [
    ["Title", data.title || "(no title)"],
    ["Description", data.description || "—"],
    ["Total Employees Voted", data.total_votes],
    ["Employees Not Voted", data.non_voters.length],
    ["Non-Voters", data.non_voters.map((n) => n.full_name).join(", ") || "—"],
    ["Multiple Choice", data.allow_multiple ? "Yes" : "No"],
    ["Created At", new Date(data.created_at).toLocaleString()],
    [
      "Closes At",
      data.closes_at ? new Date(data.closes_at).toLocaleString() : "No expiry",
    ],
    ["Export Date", new Date().toLocaleString()],
  ];
  summaryRows.forEach(([f, v], i) => {
    const r = summarySheet.addRow([f, v]);
    zebra(r, i);
    r.getCell(1).font = { bold: true };
  });

  // ── Download ─────────────────────────────────────────────────────────────
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const safeTitle = (data.title || "poll")
    .replace(/[^a-zA-Z0-9_]/g, "_")
    .substring(0, 40);
  link.download = `poll_${safeTitle}_${new Date().toISOString().split("T")[0]}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
